import type { Good, GoodId, MilestoneId, ProducedGoodId, WorldConfig, WorldRect } from "./types";

/**
 * Every balancing screw. The single source of truth.
 *
 * CAUTION: a change here moves the difficulty immediately. Run `npm test`
 * afterwards — test/balance.test.ts checks that competent play reaches each
 * milestone inside its day window and that doing nothing reaches none.
 */

/** Logische Spielfeldgröße. Das Canvas skaliert darauf, Koordinaten sind fix. */
export const WORLD_W = 1000;
export const WORLD_H = 640;
export const REGION_W = 600;
export const MAX_REGIONS = 12;
export const REGION_BASE_COST = 75_000;
export const REGION_STREAM = 0x1656_67b1;
export const BASE_WORLD_RECT: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H };

/** Sekunden Echtzeit pro Spieltag. */
export const DAY_LENGTH = 25;

/** Fixed simulation interval used by deterministic replays. */
export const SIM_TICK = 1 / 30;

/** Bump only with an explicit decision to reject existing replay slots. */
export const REPLAY_VERSION = 1;

/**
 * Ceilings on an imported replay, in ticks and in commands (RFC-0044 §5).
 *
 * A replay is untrusted input that costs real work to read: reconstruction
 * replays every tick, so a file claiming a tick far in the future would hold the
 * main thread for as long as it liked. The tick ceiling is the one that binds —
 * measured over 500 days, a competent network produces 176 commands but 375 000
 * ticks, and the ticks carry the whole cost.
 *
 * It doubles as a ceiling on how long a replay world may be played. 500 days is
 * twelve times the day the map is finished being built (RFC-0035), and a slot
 * that reaches it stops loading rather than loading slowly.
 */
export const MAX_REPLAY_TICKS = Math.ceil((500 * DAY_LENGTH) / SIM_TICK);
/** 100× the measured 500-day count: bounded, without policing how a player clicks. */
export const MAX_REPLAY_COMMANDS = 20_000;

export const START_MONEY = 6000;

export const TRUCK_COST = 1000;
/** Fixkosten je Lkw und Tag — der Grund, warum Überkapazität wehtut. */
export const TRUCK_UPKEEP = 70;
/**
 * What a truck fetches when it is given back, as a fraction of TRUCK_COST
 * (RFC-0018).
 *
 * Bounded above by two arguments that agree: a value near 1 would make a truck a
 * savings account across a fuel spike, and it would erase the punishment for
 * overcapacity that this game's second bottleneck *is*. Bounded below by
 * nothing — at 0 the button still works, because the upkeep stops either way.
 */
export const TRUCK_REFUND = 0.5;
export const TRUCK_CAPACITY = 8;
/** Weltpixel pro Sekunde. */
export const TRUCK_SPEED = 95;
export const LOAD_TIME = 1.2;
export const UNLOAD_TIME = 0.8;

/** Baukosten je Weltpixel Streckenlänge. */
export const ROUTE_COST_PER_PX = 3;

/**
 * Shared warehouse size of a transfer node, in units across all goods.
 *
 * 60 is 7.5 truckloads: enough to absorb the burst of a long inbound leg, small
 * enough that a hub nobody drains jams within a minute of play.
 */
export const TRANSFER_CAP = 60;

/**
 * World pixels of road per truck before a route starts to jam.
 *
 * Sharp: at 150 the 394 px route carries 3.6 trucks and the greedy fourth is
 * nearly free again; at 250 it carries 2.6 and the third truck — the one
 * competent play needs — starts paying. 200 is the middle of a narrow band, and
 * moving it is a balancing change that requires a full `npm test`.
 */
export const CONGESTION_SPACING = 200;
/**
 * How sharply speed falls past capacity. 2 makes total route throughput fall
 * rather than merely flatten: with an exponent of 1 the route approaches an
 * asymptote above what any producer on this map can feed, and overcapacity
 * would be free again one asymptote higher.
 */
export const CONGESTION_EXPONENT = 2;
/**
 * Hard floor on the speed multiplier, reached at exactly twice capacity. A
 * safety clamp, not a balancing knob: without it a dozen trucks look frozen
 * rather than jammed, which reads as a bug and is unrecoverable except by
 * demolishing the route.
 */
export const CONGESTION_MIN_SPEED = 0.25;

// ─── Lanes (RFC-0014) ───────────────────────────────────────────────────────

/**
 * What a lane costs to build, as a share of the trucks it makes room for.
 *
 * A lane opens `routeCapacity(L)` further truck-slots, and it is priced in those
 * slots rather than in pixels. Per pixel gets it backwards: the shortest roads
 * open the fewest slots and would be the cheapest to widen, while the shortest
 * roads — the ones out of a hub, 111 to 122 px on seeds 1..5 — are exactly the
 * ones under pressure. A 339 € lane there would delete this game's tightest
 * bottleneck for pocket money.
 *
 * Anchored on TRUCK_COST rather than on euros because the decision is *lane
 * against truck*: a tuning pass on the truck has to drag the lane with it, or
 * the comparison changes underneath the player. RFC-0014 §3.5.
 */
export const LANE_COST_FACTOR = 0.6;
/**
 * Daily upkeep of a lane, as a share of the upkeep of the fleet it enables.
 *
 * **This is the number that keeps congestion a mechanic.** Without a running
 * cost a lane *abolishes* a bottleneck instead of moving it, "widen everything"
 * becomes the answer, and `test/balance.test.ts › "Überkapazität verzögert den
 * zweiten Meilenstein"` loses its subject.
 *
 * Not multiplied by upkeepFactor(): RFC-0011's two kinds are fuel and a haulage
 * rebate, both about running a fleet. Road maintenance is a third axis, and
 * putting it on the first would re-tune „Störungen verschieben Druck, nicht
 * Geld" against a base it was never measured on. RFC-0014 §3.8.4.
 */
export const LANE_UPKEEP_FACTOR = 0.35;

/** Tägliches Nachfragewachstum je Stadt, gedeckelt durch CityNode.demandMax. */
export const DEMAND_GROWTH = 1.09;

/**
 * What a delivered unit is worth when the city was not waiting for it.
 *
 * Not zero: a truck already on the road must not become a mistake the player
 * cannot take back, and a route that occasionally overshoots is worth keeping.
 * Not one: that is the state RFC-0008 exists to fix, where the necessary route
 * and the profitable route are different routes and only one is paid for.
 *
 * Measured over three seeds and 110 days: attention was worth 15 % more money
 * at 1.0 and is worth 39 % more at 0.35.
 */
export const GLUT_FACTOR = 0.35;

/**
 * What a hauled unit is actually paid, as a share of its list price (RFC-0034).
 *
 * Measured, not chosen: 154 607 deliveries over eight maps and 110 days of
 * competent play. Two channels multiply into it and neither is a knob —
 * `trust` costs 12.5 %, `wanted = min(cargo, backlog)` against GLUT_FACTOR
 * costs 20.2 %. The surprise is the order: the glut discount takes more than
 * the trust penalty does.
 *
 * It exists so that a text or a rule can turn throughput into euros without
 * a typed 0.68 — `routeThroughput` alone is an *upper bound* on revenue, and
 * two documents built a decision on it before this was measured (RFC-0032
 * §1.5.1, RFC-0033 §1.11).
 *
 * A **mean**, and the spread around it is wide: per road p05 0.316, median
 * 0.665, p95 0.917. Good enough for the manual, not good enough for a purchase
 * — that one wants the road's own yield, which is `unitYield()` in RFC-0034 §3.2
 * and computable from state.
 */
export const MEAN_YIELD = 0.68;

/**
 * How much longer a path over a waypoint may be before the game stops offering
 * it (RFC-0033 §1.17).
 *
 * Measured, and it is the edge of a cliff rather than a taste: sharing a
 * corridor pays **+4,7 %** when the detour stays near 2 %, is roughly level at
 * 5 %, and costs **−2,2 %** at 10 %. Past that it only gets worse.
 *
 * It is a filter on what the panel *offers*, not a rule the simulation
 * enforces — the player still chooses between two real options. Offering the
 * ones that are measured losses would not be neutrality; it would be noise, and
 * it would put a choice card in front of nearly every road ever built.
 */
export const VIA_DETOUR_MAX = 1.10;

/** Obergrenze des Rückstands. Verhindert die Todesspirale. */
export const BACKLOG_CAP = 200;
/** Ab diesem Rückstand beginnt das Vertrauen zu sinken. */
export const BACKLOG_FORGIVEN = 25;
/** Rückstandsspanne, über die das Vertrauen von 1 auf TRUST_MIN fällt. */
export const BACKLOG_RANGE = 120;
export const TRUST_MIN = 0.5;
/** Wie schnell sich das Vertrauen dem Zielwert nähert (je Sekunde). */
export const TRUST_RATE = 0.5;

export const GOODS: Record<GoodId, Good> = {
  holz: { id: "holz", name: "Holz", price: 24, token: "good-holz" },
  erz: { id: "erz", name: "Erz", price: 32, token: "good-erz" },
  /**
   * The refined good (RFC-0015). Nothing produces it — a refinery makes it out
   * of one holz and one erz.
   *
   * **Measured in phase 3, and the anchor of 90 was far too low.** It was
   * computed as 56 € of raw value plus one hauling leg, on the assumption that
   * a chain costs one extra road. It costs three roads and three fleets, it
   * destroys one unit of backlog relief per unit made, and the flow it takes is
   * flow the cities were already getting (§1.1). Swept over twenty maps against
   * a player who never touches a hub: break-even sits at **238 €**, and 270
   * leaves the refiner 2,7 % ahead — thin on purpose. A gift would be a price
   * that made the chain obviously right on every map; this one is worth building
   * where the geometry is kind and not worth it where it is not.
   */
  werkzeug: { id: "werkzeug", name: "Werkzeug", price: 270, token: "good-werkzeug" },
};

export interface Milestone {
  readonly id: MilestoneId;
  /** Player-facing, hence German. Shown in the HUD and the save menu. */
  readonly label: string;
  /** Lifetime revenue in € that triggers it. */
  readonly earned: number;
}

/**
 * Ordered by threshold. The order is the roadmap order; do not sort at runtime.
 *
 * Every milestone extends the map (world.ts, extensionFor) and unlocks a
 * mechanic that the new terrain demands: verkehr → RFC-0003, umschlag →
 * RFC-0004, weites-land → RFC-0005. A milestone that only announces itself is
 * a notification, and this game does not need one.
 */
export const MILESTONES: readonly Milestone[] = [
  // Raised nominally by 15 % for RFC-0023, to round numbers rather than to the
  // factor. The knob is the idle floor, not the difficulty: doing nothing used
  // to reach „Umschlag" on day 65 against a guard at 60, and now reaches it on
  // day 76. Competent play is 2,6 % *richer*, which is the point — it keeps
  // building for longer, and every milestone window still holds with at least
  // three days to spare, so WINDOW in test/balance.test.ts does not move.
  //
  // The two knobs #74 also named were measured and are not this one.
  // DEMAND_SUPPLY_RATIO moves the idle floor the *wrong* way — 0,65 takes it to
  // day 43 — because a smaller backlog lands more of an untended truck's loads
  // at full price instead of GLUT_FACTOR. TRUCK_UPKEEP moves it not one day at
  // any value. RFC-0023 §1.3 and §1.4.
  { id: "verkehr", label: "Verkehr", earned: 11_500 },
  { id: "umschlag", label: "Umschlag", earned: 40_000 },
  { id: "weites-land", label: "Weites Land", earned: 115_000 },
  { id: "veredelung", label: "Veredelung", earned: 230_000 },
];

// ─── Cost disruptions (RFC-0011) ────────────────────────────────────────────

/**
 * Mixed into the day with Math.imul to pick a disruption stream — the golden
 * ratio constant, odd, so the multiplication is a bijection on uint32.
 *
 * CAUTION: the day is multiplied in, never added. `rng(seed + STREAM + day)`
 * makes (seed + 1, day) and (seed, day + 1) the *same* stream, so every world's
 * weather is every other world's shifted by a day — measured: seeds 1, 2 and 3
 * produced an identical list of nine disruptions over 120 days. RFC-0011 §3.3.
 */
export const DISRUPTION_STREAM = 0x9e37_79b9;
/**
 * No disruption before this day. The opening is where a player learns what a
 * truck costs, and a fuel spike in there teaches the wrong lesson.
 */
export const DISRUPTION_FIRST_DAY = 10;
/** Chance that a given day starts one. */
export const DISRUPTION_CHANCE = 0.1;
export const DISRUPTION_DAYS_MIN = 2;
/** Longest disruption, inclusive. Also bounds the look-back in disruptionsOn(). */
export const DISRUPTION_DAYS_MAX = 4;
/** Upkeep multiplier while fuel is expensive. Above 1: the fleet costs more. */
export const FUEL_FACTOR = 1.4;
/**
 * Upkeep multiplier while road haulage is compensated. Below 1: the fleet costs
 * less for a few days.
 *
 * Both kinds move the *same* axis, and that is the whole reason this one is a
 * rebate rather than a bonus on the delivery price. Measured: a price bonus is a
 * gift to a network nobody is watching — an idle world on seed 2 earns 34 214 €
 * over 100 days against a 35 000 € milestone, so any revenue multiplier above
 * 2.3 % pushes doing nothing past a milestone and the guard falls. A rebate
 * scales with the fleet instead of with revenue: an idle world with one truck
 * gains 14 € on a day it earns 350, while a mature fleet of twelve gains 840.
 *
 * What it buys in play: cheap upkeep makes a bigger fleet temporarily correct,
 * and when the rebate ends the surplus trucks are still there. A temptation, not
 * a present. RFC-0011 §3.7.
 */
export const SUBSIDY_FACTOR = 0.6;
/**
 * How far the *difference from 1* strays from its base, either way — never the
 * factor itself.
 *
 * A 1.1 factor scaled by 0.85 would be 0.935, which is a fuel spike that makes
 * trucks cheaper. Varying the surcharge (or the discount) keeps every factor on
 * its own side of 1 by construction, whatever the constants become, and leaves
 * the mean exactly at the base.
 */
export const DISRUPTION_FACTOR_SPREAD = 0.15;

// ─── Throughput disruptions (RFC-0012) ──────────────────────────────────────

/**
 * A stream of its own, not DISRUPTION_STREAM.
 *
 * CAUTION: an extra draw inside disruptionsOn() would shift its stream and
 * silently rewrite the fuel history of every world that already exists. The two
 * systems therefore share the mixing trick and nothing else. RFC-0012 §3.3, and
 * test/disruption.test.ts hashes the RFC-0011 history to prove it.
 */
export const THROUGHPUT_STREAM = 0x85eb_ca6b;
/**
 * Later than DISRUPTION_FIRST_DAY: a closure before the player owns a second
 * route is not a setback, it is a full stop.
 */
export const THROUGHPUT_FIRST_DAY = 20;
/**
 * Lower than DISRUPTION_CHANCE, because these hurt more and because both systems
 * run at once. Measured against the day-110 mean in RFC-0012 §7.
 */
export const THROUGHPUT_CHANCE = 0.07;
export const THROUGHPUT_DAYS_MIN = 2;
/** Longest one, inclusive. Also bounds the look-back in throughputOn(). */
export const THROUGHPUT_DAYS_MAX = 4;
/**
 * What is left of a producer's rate while it is down.
 *
 * **Not a free knob.** Solvability needs `Σ demandMax < Σ rate`, and RFC-0005
 * allots the left side at DEMAND_SUPPLY_RATIO of the right, so the headroom is
 * 20 % of production. Measured over seeds 1..2000 at all four stages, a single
 * producer owns up to 33,0 % of it — four producers on the start map, so one of
 * them can be a third of everything. A full stop would take more than the
 * headroom and make the map unsolvable, which is why an outage is a factor and
 * not a switch:
 *
 *     0,330 × (1 − 0,5) = 0,165 < 0,200
 *
 * The floor is 1 − 0,200/0,330 = 0,394. Below it the balancing guard fails, and
 * it fails with the arithmetic in the message. RFC-0012 §3.6.
 */
export const OUTAGE_FACTOR = 0.5;
/**
 * What is left of a city's demand during a slump. Free to tune: less demand
 * moves the solvability inequality the safe way, and the punishment it carries
 * is RFC-0008's — a load nobody waited for pays GLUT_FACTOR.
 */
export const SLUMP_FACTOR = 0.5;
/**
 * What is left of a truck's speed on a closed route. Free to tune for the same
 * reason: a closure throttles one edge, and the player has others.
 */
export const CLOSURE_FACTOR = 0.5;

// ─── Demand shift (RFC-0016) ────────────────────────────────────────────────

/**
 * A third stream, and for the third time the same warning: an extra draw inside
 * disruptionsOn() or throughputOn() would shift theirs and silently rewrite the
 * weather of every world that already exists. The hash in
 * test/disruption.test.ts is what catches it.
 *
 * The day is multiplied in, never added — see DISRUPTION_STREAM.
 */
export const DEMAND_STREAM = 0xc2b2_ae35;
/**
 * No shift before this day. The opening is where the map is being learned, and
 * an appetite that turns before the player has served it once teaches nothing.
 * Later than both weather kinds, because this one invalidates a *road*, not a
 * bill.
 */
export const DEMAND_SHIFT_FIRST_DAY = 15;
/**
 * Chance that a given day swaps two cities. Lower than every other kind: the
 * others cost money for a few days, this one can make a road worthless until the
 * player rebuilds it.
 *
 * **Swept, and the result is a cliff rather than a slope.** Mean trust after 100
 * days: 0.889 at 0.005 and at 0.01, then 0.67–0.72 at both 0.02 and 0.04 — the
 * same value a world without the nearest-good assignment produced (RFC-0016
 * §1.3). One swap a world absorbs; two it does not, because a swapped city is by
 * construction a city that is no longer next to its good, and nothing moves it
 * back. 0.01 is the *highest* value that holds every guard, which is the rule
 * here: this is a mechanic, so it should be as present as the game can carry.
 *
 * The milestone windows did not move at any value — what this constant buys and
 * costs is trust and money, not the clock.
 */
export const DEMAND_SHIFT_CHANCE = 0.01;

// ─── A map that keeps changing (RFC-0017) ───────────────────────────────────

/** Units per second one upgrade adds to a producer. */
export const PRODUCER_UPGRADE_STEP = 0.2;
/**
 * What the first upgrade of a producer costs.
 *
 * Anchored on what the step earns rather than picked: +0.2 u/s at the cheaper
 * good's 24 € is 0.2 × DAY_LENGTH × 24 = 120 € a day, so 4 000 € pays itself
 * back in about a month of play — long enough to be a decision, short enough
 * that a network with money has a reason to make it.
 */
export const PRODUCER_UPGRADE_COST = 4000;
/**
 * What each further upgrade of the *same* producer multiplies that by.
 *
 * Rising, or the endgame is a button held down. **How fast** decides how long
 * the game is, and it is arithmetic rather than taste: a step is worth
 * `PRODUCER_UPGRADE_STEP × DAY_LENGTH × price × MEAN_YIELD` whatever the level,
 * so a player who buys what pays back inside `PAYBACK` days can afford
 * `log_growth(PAYBACK × Schrittwert / PRODUCER_UPGRADE_COST)` of them. At 1,8
 * that is two; at 1,3 it is four; measured over SEEDS × 300 days, only 1,15
 * carries the ladder to level 8 on every seed, which is where the payback wall
 * stands (RFC-0035 §9.1).
 *
 * Not 1,0, and that is the interesting end: without growth the ladder has no
 * brake at all — a works reaches level 1002 to 1659, the map's rate goes from
 * 20 to 154-363 u/s and the player earns 10-16 million against a baseline of
 * 3,2. A lane is too cheap against that gain to be the brake, so the price has
 * to be; the road only decides whether a step is *allowed* (RFC-0035 §3.2).
 *
 * Lowered from 1,8 with RFC-0035. Before it, canUpgrade() checked against a
 * single lane and allowed a median of two steps, so a cheaper price bought
 * nothing — the rule refused first.
 */
export const PRODUCER_UPGRADE_COST_GROWTH = 1.15;
/**
 * What commissioning a whole new producer costs (RFC-0017 §3.4).
 *
 * Anchored on the upgrade rather than picked, so the two levers stay comparable:
 * an upgrade buys PRODUCER_UPGRADE_STEP for PRODUCER_UPGRADE_COST, which is
 * 20 000 € per unit per second, and a fresh producer arrives at about
 * PRODUCER_RATE — roughly two units a second, minus whatever its road caps away.
 * That would be 40 000 €; 35 000 is the discount for the parts the buyer still
 * has to pay for afterwards, a road out of the strip and the fleet on it.
 *
 * Flat, unlike the upgrade curve. What stops the player buying producers all day
 * is not the price but `drainable()`: every new one arrives in a strip and needs
 * a city that wants its good, and the map runs out of both.
 */
export const PRODUCER_BUILD_COST = 35_000;

/**
 * The stream the world's own events come from (RFC-0017 §3.5).
 *
 * Its own number, and nothing above it moves: an extra draw inside
 * disruptionsOn() would rewrite the weather of every world that already exists,
 * and test/disruption.test.ts hashes that history to catch exactly that.
 */
export const WORLD_STREAM = 0x27d4_eb2f;
/**
 * No producer closes before this day.
 *
 * After the map is finished, not during: competent play has the last milestone
 * by day 46, and a works that shuts while the board is still growing takes away
 * a corner the player has not been given time to serve. It is also what this
 * whole document is for — a lever for the map that is *done* (§1.2).
 */
export const RETIRE_FIRST_DAY = 60;
/**
 * Chance that a given day closes one producer.
 *
 * The rarest of the four scheduled things in this game, and it has to be: fuel,
 * a rebate, an outage and an appetite swap all end on their own, and this one
 * never does. One closure in about two hundred days, against a map that has
 * twelve works on it.
 */
export const RETIRE_CHANCE = 0.005;

// ─── Map generation (RFC-0005) ──────────────────────────────────────────────

/**
 * The order goods are handed out **to producers** in. Explicit rather than
 * `Object.keys(GOODS)`, which would make key order load-bearing for every map
 * ever generated.
 *
 * CAUTION: not the same list as GOOD_IDS_ALL, and the two must not be unified.
 * RFC-0015 adds werkzeug, which nothing produces — putting it here would hand a
 * producer a good it cannot make. `ProducedGoodId` is what makes that a compile
 * error rather than a map nobody can explain.
 */
export const GOOD_IDS: readonly ProducedGoodId[] = ["holz", "erz"];

/**
 * Every good that exists, for the loops that walk a full record: a city's
 * demand, its backlog, a hub's stock. See GOOD_IDS for why this is a second
 * name rather than the same one.
 */
export const GOOD_IDS_ALL: readonly GoodId[] = ["holz", "erz", "werkzeug"];

/**
 * How many cities want a *second* good (RFC-0016 §3.6).
 *
 * **Off, and measured that way rather than assumed.** Swept over 0..3: trust did
 * not move between 0, 1 and 2 and fell at 3; the milestone windows widened with
 * every step up, „weites-land" going out of reach entirely at 2 and 3 on some
 * seeds; and the day-110 mean fell from 375 k to 113 k. Zero is not the timid
 * choice here, it is the best one on every axis that was measured.
 *
 * Kept as a constant rather than deleted, because BACKLOG_CAP being *per good*
 * is the shape RFC-0015 needs the day werkzeug exists — and then this knob is
 * how much that costs, ready to be swept again.
 *
 * Counted over the cities a world has ever placed, in id order, so a growth step
 * cannot change what an existing city wants — only a swap does that, and a swap
 * exchanges whole records (RFC-0016 §3.5).
 */
export const TWO_GOOD_CITIES = 0;

/**
 * What one unit of werkzeug consumes (RFC-0015 §3.2).
 *
 * Keyed on the *inputs* only, never on GoodId: a full record would need
 * `werkzeug: 0`, and the conversion divides by these numbers.
 */
export const RECIPE: Readonly<Record<"holz" | "erz", number>> = { holz: 1, erz: 1 };
/**
 * Units of werkzeug a refinery makes per second, given both inputs.
 *
 * It is also what the generator treats as a refinery's *rate*: werkzeug demand
 * is allotted as `Σ REFINERY_RATE × DEMAND_SUPPLY_RATIO`, the same shape as
 * every other good, and paid for out of both input budgets (RFC-0015 §1.2).
 * The conversion that spends it is phase 3; the budget it sizes is phase 2.
 */
export const REFINERY_RATE = 0.6;

/** Base output before jitter and before the drain cap. The hand map's values. */
export const PRODUCER_RATE: Record<ProducedGoodId, number> = { holz: 2.2, erz: 1.8 };
export const PRODUCER_CAP = 60;
/** How far a rate may stray from its base, either way. */
export const RATE_JITTER = 0.15;
/**
 * Safety factor on the drain cap. A producer whose rate exactly equalled what
 * its best route can carry would need a perfect fleet forever; 0.95 leaves the
 * player somewhere to stand.
 */
export const DRAIN_MARGIN = 0.95;
/** Seconds of production a fresh producer starts with in its store. */
export const START_STOCK_SECONDS = 5;

/**
 * Peak demand as a share of production. The single knob for global pressure —
 * the hand map's 6.4 / 8.0, now true by construction instead of by luck.
 */
export const DEMAND_SUPPLY_RATIO = 0.8;
/** How unevenly the demand budget is split between cities. */
export const DEMAND_SPREAD = 0.25;
/** Where a city's demand starts, as a share of its maximum. */
export const DEMAND_START_FRACTION = 0.57;

/** Player-facing, hence German. Enough entries that a full world never wraps. */
export const PRODUCER_NAMES: Record<ProducedGoodId, readonly string[]> = {
  holz: [
    "Sägewerk Nord", "Sägewerk Süd", "Waldlager Ost", "Forsthof", "Holzplatz Weide",
    "Sägewerk Hochland", "Waldlager Nordwest", "Bretterhof", "Schnittholz Au", "Kiefernkamp",
  ],
  erz: [
    "Mine Ost", "Mine Süd", "Erzgrube", "Klippenmine", "Schachtanlage",
    "Erzgrube Südost", "Mine Nordwest", "Tiefstollen", "Hüttenberg", "Eisenkaute",
  ],
};

export const CITY_NAMES: readonly string[] = [
  "Zentralstadt", "Hafen Bregen", "Südhafen", "Talstadt", "Moorstadt",
  "Kreuzhafen", "Nordfeste", "Ostmarkt", "Weststadt", "Furtberg", "Anger", "Steinbach",
];

export const TRANSFER_NAMES: readonly string[] = [
  "Umschlag West", "Umschlag Ost", "Umschlag Mitte", "Umschlag Nord", "Umschlag Süd",
];

/**
 * The opening map. `minDistance` is the widest of the four because the board is
 * empty: a sparse start puts a floor under route length, and a long first route
 * is what makes the opening purchase a decision.
 */
export const START_CONFIG: WorldConfig = {
  producers: 4,
  cities: 3,
  transfers: 0,
  margin: 80,
  minDistance: 150,
  cityBand: 0.4,
  producerBand: 0.34,
  tries: 500,
  bias: "none",
};

/**
 * What each milestone adds, and what for. The counts are RFC-0002's; the briefs
 * are how those arguments survive a random map (RFC-0005 §3.3).
 *
 * `minDistance` tightens and `cityBand` widens as the board fills. Both are
 * arithmetic, not taste: 21 nodes at 150 px apart do not fit in an 820 × 460
 * field, and nine cities at 100 px do not fit in a 400 px band. A generator that
 * throws on the third milestone is worse than a crowded map.
 */
export const EXTENSION_CONFIG: Record<MilestoneId, WorldConfig> = {
  // A short producer → city pair, because that is where a player stacks trucks
  // and congestion has to have a stack to bite on.
  "verkehr": { ...START_CONFIG, producers: 1, cities: 1, minDistance: 130, cityBand: 0.44, bias: "tight", tightTarget: 210 },
  // Producers far from every city, plus the two hubs that answer them.
  "umschlag": { ...START_CONFIG, producers: 2, cities: 2, transfers: 2, minDistance: 110, cityBand: 0.5, bias: "wide" },
  // Nothing left to teach; this one fills the board.
  "weites-land": { ...START_CONFIG, producers: 3, cities: 3, minDistance: 100, cityBand: 0.56, bias: "none" },
  // Two refineries and the goods to feed them. Two cities rather than one,
  // because the two producers make one holz and one erz between them and a
  // single fresh city picks exactly one good — measured over 500 seeds, that
  // left the other good with no taker on all 500 and pulled the demand ratio
  // from 0.800 down to 0.713 (RFC-0015 §3.5). `tries` is the price of the last
  // seed of 2000: at 500 the board is crowded enough to lose a node.
  "veredelung": {
    ...START_CONFIG,
    producers: 2, cities: 2, transfers: 2,
    minDistance: 95, cityBand: 0.56, bias: "none", tries: 2000, refines: true,
  },
};
